import Snowplow, { WebVitalsReportDataEntry } from 'components/analytics/conversions/snowplow';
import type {
  UserStateSchemaData,
  ResourceImpressionsData,
  UserState,
  TurbolinksTimingData,
  WebVitalsReportData,
} from 'components/analytics/conversions/snowplow';
import { parseBoolean, getCurrentPageParam, featureEnabled } from 'lib/helpers';
import { getAnalyticsDimensions, getPageviewGuid } from 'components/analytics/conversions/tracking';
import Cookies from 'js-cookie';

// New schema versions must be deployed in iglu as well as updated in
// the snowplow Lambda processor before any events are emitted or else the
// events will be marked invalid during processing
export const USER_STATE_SCHEMA = 'iglu:com.eezy/user_state/jsonschema/1-0-7';
export const RESOURCE_IMPRESSIONS_SCHEMA = 'iglu:com.eezy/resource_impressions/jsonschema/1-0-0';
export const TURBOLINKS_TIMING_SCHEMA = 'iglu:com.eezy/turbolinks_timing/jsonschema/1-0-0';
export const WEB_VITALS_SCHEMA = 'iglu:com.eezy/web_vitals/jsonschema/1-0-1';

// #region Types
type TurbolinksTimingEvent = CustomEvent & {
  data: {
    url:string
    timing: {
      visitStart:number
      requestStart:number
      requestEnd:number
      visitEnd:number
    }
  }
}

type PositionCoordinates = {
  x:number
  y:number
  width:number
  height:number
  top:number
  right:number
  left:number
  bottom:number
}

type WebVitalReport = {
  name:string
  value:number
  delta:number
  id:string
  isFinal:boolean
}

type FCPWebVitalReportEntry = {
  name:string
  entryType:string
  startTime:number
  duration:number
  processingStart:number
  processingEnd:number
}
type FCPWebVitalReport = WebVitalReport & {
  name:'FCP'
  entries:FCPWebVitalReportEntry[]
}

type LCPWebVitalReportEntry = {
  name:string
  entryType:string
  startTime:number
  duration:number
  size:number
  renderTime:number
  loadTime:number
  firstAnimatedFrameTime:number
  id:string
  url:string
  element:HTMLElement
}
type LCPWebVitalReport = WebVitalReport & {
  name:'LCP'
  entries:LCPWebVitalReportEntry[]
}

type CLSWebVitalReportEntry = {
  name:string
  entryType:string
  startTime:number
  duration:number
  value:number
  hadRecentInput:boolean
  lastInputTime:number
  sources: {
    previousRect:PositionCoordinates
    currentRect:PositionCoordinates
    node:HTMLElement
  }[]
}
type CLSWebVitalReport = WebVitalReport & {
  name:'CLS'
  entries:CLSWebVitalReportEntry[]
}

type WebVitalsReportEvent = CustomEvent & {
  detail: FCPWebVitalReport|LCPWebVitalReport|CLSWebVitalReport
}
// #endregion Types

export default function SnowplowTracker({ collector, appId }) {
  let TurbolinksVisits = 0;
  let TurbolinksPreviousUrl = '';

  const getResourceImpressionsData = () => {
    const data:ResourceImpressionsData = {
      ids: [],
      pros: [],
      page: 0,
    };
    const page = getCurrentPageParam();

    document.querySelectorAll('[data-item-id]').forEach((resourceEl:HTMLElement) => {
      const { itemId, pro } = resourceEl.dataset;

      data.ids.push(parseInt(itemId, 10));

      // use {0,1} instead of booleans to save space in the JSON
      if (parseBoolean(pro)) {
        data.pros.push(1);
      } else {
        data.pros.push(0);
      }
    });

    if (page) {
      data.page = parseInt(page, 10);
    } else {
      delete data.page;
    }

    return data;
  };

  // ALL changes to fields returned from getUserState require an update of the json schema stored in iglu,
  // as well as an update of the snowplow Lambda processor. Multiple versions can be in production simultaneously,
  // but new versions must be deployed to the other systems before emitting any events or else the entire event
  // will be marked invalid during processing
  type GetUserStateArgs = { includeFlags?:boolean, includeExpandedDataset?:boolean, errorText?:string };
  const getUserState = ({
    includeFlags = false,
    includeExpandedDataset = false,
    errorText = null,
  }:GetUserStateArgs = {}):UserState => {
    const {
      bot_score: botScore,
      feature_flags: flags,
      ja3_hash: ja3Hash,
      js_detect_passed: jsDetectPassed,
      sign_in_type: type,
      split_test: splitTest,
      user_auth_id: userAuthId,
      user_status_start: start,
      user_status_updated: updated,
      verified_bot: verifiedBot,
    } = getAnalyticsDimensions();

    const [splitTestName, splitTestVariant] = splitTest.split(':');
    const pageviewGuid = getPageviewGuid();

    const payload:UserStateSchemaData = { pageviewGuid, fingerprint: Cookies.get('apolloFP') };

    if (includeFlags) {
      payload.flags = flags;
    }

    if (errorText) {
      payload.errorText = errorText;
    }

    if (includeExpandedDataset) {
      payload.botScore = botScore;
      payload.ja3Hash = ja3Hash;
      payload.jsDetectPassed = jsDetectPassed;
      payload.signInType = type;
      payload.splitTestName = splitTestName;
      payload.splitTestVariant = splitTestVariant;
      payload.statusStart = start;
      payload.statusUpdated = updated;
      payload.userAuthId = userAuthId;
      payload.verifiedBot = verifiedBot;
    }

    return [{
      schema: USER_STATE_SCHEMA,
      data: payload,
    }];
  };

  const fireResourceImpressionsEvent = () => {
    const data = getResourceImpressionsData();

    if (data.ids.length > 0) {
      Snowplow.trackSelfDescribingEvent({
        schema: RESOURCE_IMPRESSIONS_SCHEMA,
        eventData: data,
        userStateData: getUserState(),
      });
    }
  };

  // eslint-disable-next-line arrow-body-style
  const isValidTurbolinkVisit = () => TurbolinksVisits >= 1;

  const collectTurbolinksTiming = (event:TurbolinksTimingEvent) => {
    if (!isValidTurbolinkVisit()) return false;

    const turbolinksTimingEvent:TurbolinksTimingData = {
      visitDur: event.data.timing.visitEnd - event.data.timing.visitStart,
      reqDur: event.data.timing.requestEnd - event.data.timing.requestStart,
      prevUrl: TurbolinksPreviousUrl,
      visitNo: TurbolinksVisits,
    };

    Snowplow.trackSelfDescribingEvent({
      schema: TURBOLINKS_TIMING_SCHEMA,
      eventData: turbolinksTimingEvent,
      userStateData: getUserState(),
    });

    return true;
  };

  const loadListener = (event:TurbolinksTimingEvent) => {
    this.trackPageView();
    fireResourceImpressionsEvent();
    collectTurbolinksTiming(event);
  };

  const isValidWebVitalsReport = (vitalsEvent:WebVitalsReportEvent) => {
    if (TurbolinksVisits > 0) return false;

    const { detail }:{ detail:WebVitalReport } = vitalsEvent;

    if (detail.isFinal === false && detail.name !== 'CLS') return false;
    if (detail.name === 'CLS' && detail.value === 0) return false;

    return true;
  };

  const shiftCLSvalue = (clsScore:number) => Math.trunc(clsScore * 100);

  const nodeElementToString = (element:HTMLElement) => {
    if (!element) return '';

    const parent = element.parentNode as HTMLElement;
    let tagString = `${element.tagName}`;

    if (element.classList) {
      tagString += `.${element.classList.toString()}`;
    }

    // only get the parent if it has classes, otherwise it won't tell us much
    if (parent && parent.classList && parent.classList.toString() !== '') {
      tagString = `${parent.tagName}.${parent.classList.toString()} > ${tagString}`;
    }

    return tagString.substring(0, 255);
  };

  const collectCLSSources = (entry:CLSWebVitalReportEntry) => {
    const sourceNodes:string[] = [];
    entry.sources.forEach((source) => {
      sourceNodes.push(nodeElementToString(source.node));
    });

    return sourceNodes;
  };

  const transformWebVitalsReport = (eventDetail:WebVitalReport) => {
    const transformedWebVitalsReport:WebVitalsReportData = {
      name: eventDetail.name,
      val: (eventDetail.name !== 'CLS') ? Math.trunc(eventDetail.value) : shiftCLSvalue(eventDetail.value),
      entries: [],
    };

    // eslint-disable-next-line default-case
    switch (transformedWebVitalsReport.name) {
      case 'LCP':
        (eventDetail as LCPWebVitalReport).entries.forEach((entry) => {
          const newEntry:WebVitalsReportDataEntry = {
            val: Math.trunc(entry.size),
            url: entry.url.substring(0, 255),
            el: nodeElementToString(entry.element),
            load: Math.trunc(entry.loadTime),
            sTime: Math.trunc(entry.startTime),
            render: Math.trunc(entry.renderTime),
          };

          transformedWebVitalsReport.entries.push(newEntry);
        });
        break;

      case 'FCP':
        (eventDetail as FCPWebVitalReport).entries.forEach((entry) => {
          const newEntry:WebVitalsReportDataEntry = {
            name: entry.name,
            dur: Math.trunc(entry.duration),
            sTime: Math.trunc(entry.startTime),
            sProcess: Math.trunc(entry.processingStart),
            eProcess: Math.trunc(entry.processingEnd),
          };

          transformedWebVitalsReport.entries.push(newEntry);
        });
        break;

      case 'CLS':
        (eventDetail as CLSWebVitalReport).entries.forEach((entry) => {
          const newEntry:WebVitalsReportDataEntry = {
            dur: Math.trunc(entry.duration),
            sTime: Math.trunc(entry.startTime),
            recentIn: entry.hadRecentInput,
            lastIn: Math.trunc(entry.lastInputTime),
            val: shiftCLSvalue(entry.value),
          };

          // Collect element names from the CLS event
          newEntry.els = [];
          if (featureEnabled('web_vitals_cls_sources')) {
            newEntry.els = collectCLSSources(entry);
          }

          transformedWebVitalsReport.entries.push(newEntry);
        });
        break;
    }

    return transformedWebVitalsReport;
  };

  const trackWebVitalsReport = (event:WebVitalsReportEvent) => {
    if (!isValidWebVitalsReport(event)) return;

    const transformedWebVitalsReport = transformWebVitalsReport(event.detail);

    Snowplow.trackSelfDescribingEvent({
      schema: WEB_VITALS_SCHEMA,
      eventData: transformedWebVitalsReport,
      userStateData: getUserState(),
    });
  };

  this.trackEvent = ({
    category,
    action,
    label = null,
    value = null,
    contentType = null,
    errorText = null,
  }) => {
    Snowplow.trackStructEvent({
      category,
      action,
      label,
      property: contentType,
      value,
      customContext: getUserState({ errorText }),
    });
  };

  this.trackPageView = () => {
    Snowplow.trackPageView({
      contextFunc: () => getUserState({ includeFlags: true, includeExpandedDataset: true }),
    });
  };

  this.init = () => {
    Snowplow.newTracker({
      namespace: 'cf',
      collectorEndpoint: collector,
      config: {
        appId,
        discoverRootDomain: true,
        eventMethod: 'beacon',
        contexts: {
          webPage: true,
          performanceTiming: true,
          gaCookies: true,
          geolocation: false,
        },
      },
    });

    Snowplow.setUserIdFromCookie({ cookieId: 'session_guid' });
    Snowplow.enableActivityTracking({
      minimumVisitLength: 10,
      heartbeatDelay: 10,
    });
    Snowplow.enableLinkClickTracking();

    window.addEventListener('turbolinks:visit', () => {
      TurbolinksVisits += 1;
    });

    window.addEventListener('turbolinks:request-start', () => {
      TurbolinksPreviousUrl = window.location.pathname;
    });

    window.addEventListener('turbolinks:load', loadListener);

    if (!featureEnabled('web_vitals_snowplow')) return;
    window.addEventListener('snowplow:web_vitals_report', trackWebVitalsReport);
  };

  this.disconnect = () => {
    window.removeEventListener('turbolinks:load', loadListener);
    window.removeEventListener('snowplow:web_vitals_report', trackWebVitalsReport);
  };
}
